import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple in-memory cache for performance
const cache = new Map();
const CACHE_TTL = 30000; // 30 seconds

const getCached = (key) => {
  const item = cache.get(key);
  if (item && Date.now() - item.timestamp < CACHE_TTL) {
    return item.data;
  }
  cache.delete(key);
  return null;
};

const setCache = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
};

const clearCache = (pattern) => {
  if (pattern) {
    for (const key of cache.keys()) {
      if (key.includes(pattern)) cache.delete(key);
    }
  } else {
    cache.clear();
  }
};

// Configure axios defaults for better performance
axios.defaults.timeout = 15000; // 15 second timeout

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Books API
export const getBooks = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, value);
    }
  });
  const cacheKey = `books_${params.toString()}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;
  
  const response = await axios.get(`${API}/books?${params.toString()}`);
  setCache(cacheKey, response.data);
  return response.data;
};

export const getBook = async (bookId) => {
  const cacheKey = `book_${bookId}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;
  
  const response = await axios.get(`${API}/books/${bookId}`);
  setCache(cacheKey, response.data);
  return response.data;
};

export const createBook = async (bookData) => {
  clearCache('books');
  const response = await axios.post(`${API}/books`, bookData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateBook = async (bookId, bookData) => {
  clearCache('book');
  const response = await axios.put(`${API}/books/${bookId}`, bookData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteBook = async (bookId) => {
  clearCache('book');
  const response = await axios.delete(`${API}/books/${bookId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getMyBooks = async () => {
  const response = await axios.get(`${API}/my-books`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Reactivate book listing
export const reactivateBook = async (bookId) => {
  const response = await axios.post(`${API}/books/${bookId}/reactivate`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Transactions API
export const initiatePurchase = async (bookId) => {
  const response = await axios.post(`${API}/buy`, {
    book_id: bookId,
    origin_url: window.location.origin
  }, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Alias for embedded checkout
export const buyBook = async (bookId, originUrl) => {
  const response = await axios.post(`${API}/buy`, {
    book_id: bookId,
    origin_url: originUrl || window.location.origin
  }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getPaymentStatus = async (sessionId) => {
  const response = await axios.get(`${API}/transactions/status/${sessionId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const validateCode = async (transactionId, code) => {
  const response = await axios.post(`${API}/validate_code`, {
    transaction_id: transactionId,
    code: code
  }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const cancelTransaction = async (transactionId) => {
  const response = await axios.post(`${API}/cancel_transaction`, {
    transaction_id: transactionId
  }, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Seller cancellation
export const sellerCancelTransaction = async (transactionId) => {
  const response = await axios.post(`${API}/transactions/seller-cancel`, {
    transaction_id: transactionId
  }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getMyPurchases = async () => {
  const response = await axios.get(`${API}/transactions/my-purchases`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getMySales = async () => {
  const response = await axios.get(`${API}/transactions/my-sales`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getTransaction = async (transactionId) => {
  const response = await axios.get(`${API}/transactions/${transactionId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Ratings API
export const createRating = async (transactionId, rating, comment) => {
  const response = await axios.post(`${API}/ratings`, {
    transaction_id: transactionId,
    rating,
    comment
  }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getSellerRatings = async (sellerId) => {
  const response = await axios.get(`${API}/ratings/seller/${sellerId}`);
  return response.data;
};

export const getTransactionRating = async (transactionId) => {
  const response = await axios.get(`${API}/ratings/transaction/${transactionId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Notifications API
export const getNotifications = async () => {
  const response = await axios.get(`${API}/notifications`, {
    headers: getAuthHeader()
  });
  return response.data.notifications || [];
};

export const markNotificationRead = async (notificationId) => {
  const response = await axios.post(`${API}/notifications/${notificationId}/read`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const markAllNotificationsRead = async () => {
  const response = await axios.post(`${API}/notifications/mark-all-read`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Admin API
export const getAdminStats = async () => {
  const response = await axios.get(`${API}/admin/stats`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getAllUsers = async () => {
  const response = await axios.get(`${API}/admin/users`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getAllBooks = async () => {
  const response = await axios.get(`${API}/admin/books`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getAllTransactions = async () => {
  const response = await axios.get(`${API}/admin/transactions`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateCommission = async (percentage) => {
  const response = await axios.put(`${API}/admin/commission`, { percentage }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminDeleteBook = async (bookId) => {
  const response = await axios.delete(`${API}/admin/books/${bookId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const adminSuspendUser = async (userId, reason = null) => {
  const response = await axios.post(`${API}/admin/users/${userId}/suspend`, 
    { reason },
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const adminUnsuspendUser = async (userId) => {
  const response = await axios.post(`${API}/admin/users/${userId}/unsuspend`, 
    {},
    { headers: getAuthHeader() }
  );
  return response.data;
};

export const adminGetUserDetails = async (userId) => {
  const response = await axios.get(`${API}/admin/users/${userId}/details`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Stripe Connect API
export const createStripeConnectAccount = async () => {
  const response = await axios.post(`${API}/stripe/connect/onboard`, {
    return_url: `${window.location.origin}/profile?stripe_return=true`,
    refresh_url: `${window.location.origin}/profile?stripe_refresh=true`
  }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getStripeConnectStatus = async () => {
  const response = await axios.get(`${API}/stripe/connect/status`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getStripeDashboardLink = async () => {
  const response = await axios.get(`${API}/stripe/connect/dashboard-link`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Seller Balance & Withdrawals
export const getSellerBalance = async () => {
  const response = await axios.get(`${API}/seller/balance`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const withdrawFunds = async () => {
  const response = await axios.post(`${API}/seller/withdraw`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Chat API
export const getChatStatus = async (transactionId) => {
  const response = await axios.get(`${API}/chat/${transactionId}/status`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getChatMessages = async (transactionId) => {
  const response = await axios.get(`${API}/chat/${transactionId}/messages`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const sendChatMessage = async (transactionId, content) => {
  const response = await axios.post(`${API}/chat/${transactionId}/send`, {
    content
  }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getTotalUnreadMessages = async () => {
  const response = await axios.get(`${API}/chat/unread-count`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Password Reset API
export const forgotPassword = async (email) => {
  const response = await axios.post(`${API}/auth/forgot-password`, { 
    email,
    origin_url: window.location.origin
  });
  return response.data;
};

export const verifyResetToken = async (token) => {
  const response = await axios.post(`${API}/auth/verify-reset-token`, { token });
  return response.data;
};

export const resetPassword = async (token, newPassword) => {
  const response = await axios.post(`${API}/auth/reset-password`, {
    token,
    new_password: newPassword
  });
  return response.data;
};

// Availability Check API
export const checkAvailability = async (bookId) => {
  const response = await axios.post(`${API}/books/${bookId}/check-availability`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const confirmAvailability = async (checkId) => {
  const response = await axios.post(`${API}/availability-checks/${checkId}/confirm`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getAvailabilityStatus = async (bookId) => {
  const response = await axios.get(`${API}/books/${bookId}/availability-status`);
  return response.data;
};

export const getMyAvailabilityChecks = async () => {
  const response = await axios.get(`${API}/my/availability-checks`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Admin Activity Settings API
export const getActivitySettings = async () => {
  const response = await axios.get(`${API}/admin/activity-settings`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateActivitySettings = async (enabled, inactivityPeriodDays = 180) => {
  const response = await axios.put(`${API}/admin/activity-settings`, {
    enabled,
    inactivity_period_days: inactivityPeriodDays
  }, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Bug Report API
export const submitBugReport = async (bugData) => {
  const response = await axios.post(`${API}/bug-reports`, bugData, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getBugReports = async () => {
  const response = await axios.get(`${API}/admin/bug-reports`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateBugReportStatus = async (reportId, status) => {
  const response = await axios.put(`${API}/admin/bug-reports/${reportId}`, { status }, {
    headers: getAuthHeader()
  });
  return response.data;
};


// Marketing Assets
export const getMarketingAssets = async () => {
  const response = await axios.get(`${API}/admin/marketing-assets`, {
    headers: getAuthHeader()
  });
  return response.data;
};
